import React, { useState, useEffect } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

interface Company {
  id: number | string;
  company_name: string;
  address?: string;
  pancard?: string;
  city?: string;
  state?: string;
  country?: string;
  gst_number?: string;
  gst_uin?: string;
  code?: string;
  cin?: string;
  email?: string;
  HSN_SAC?: string;
  GST_SLAB?: string;
  signature?: string;
  [key: string]: any;
}

interface Customer {
  id: number | string;
  company_name: string;
  city?: string;
  state?: string;
  gst_number?: string;
  gst_uin?: string;
  [key: string]: any;
}

interface InvoiceDB {
  id?: number | string;
  Company_id: string | number;
  Customer_id: string | number;
  InvoiceNo: string;
  Date: string;
  Particulars: string;
  HSN_SAC: string | number;
  SGSTAmount: number;
  CGSTAmount: number;
  IGSTAmount: number;
  Total_Amount: number;
  GST_RATE?: string | number;
  paid?: boolean | number;
  cancelled?: boolean | number;
  remarks?: string;
  // Followup: explicit Amounts column
  Amounts?: string | string[];
  hsn_codes?: string | string[];
}

function getInvoiceRowsFromDB(inv: InvoiceDB) {
  if (!inv) return [];

  // Extract particulars (array or JSON string)
  let particularsArr: string[] = [];
  if (
    typeof inv.Particulars === "string" &&
    inv.Particulars.trim().startsWith("[") &&
    inv.Particulars.includes('"')
  ) {
    try {
      const arr = JSON.parse(inv.Particulars);
      if (Array.isArray(arr)) particularsArr = arr.map(String);
    } catch {}
  }
  if (!particularsArr.length && typeof inv.Particulars === "string") {
    particularsArr = [inv.Particulars];
  }

  // Extract HSN array
  let hsnArr: string[] = [];
  let _hsnRaw: any = (inv as any).hsn_codes !== undefined ? (inv as any).hsn_codes : inv.HSN_SAC;
  if (
    typeof _hsnRaw === "string" &&
    _hsnRaw.trim().startsWith("[") &&
    _hsnRaw.includes('"')
  ) {
    try {
      const arr = JSON.parse(_hsnRaw);
      if (Array.isArray(arr)) hsnArr = arr.map(String);
    } catch {}
  }
  if (!hsnArr.length && _hsnRaw) {
    hsnArr = [String(_hsnRaw)];
  }

  // Extract Amounts array (as per followup, from Amounts column)
  let amountArr: string[] = [];
  let _amtsRaw: any = (inv as any).Amounts;
  if (
    typeof _amtsRaw === "string" &&
    _amtsRaw.trim().startsWith("[") &&
    _amtsRaw.includes('"')
  ) {
    try {
      const arr = JSON.parse(_amtsRaw);
      if (Array.isArray(arr)) amountArr = arr.map(String);
    } catch {}
  } else if (
    typeof _amtsRaw === "string" &&
    _amtsRaw.trim().startsWith("[") &&
    _amtsRaw.includes(",")
  ) {
    try {
      const arr = JSON.parse(_amtsRaw);
      if (Array.isArray(arr)) amountArr = arr.map(String);
    } catch {}
  } else if (Array.isArray(_amtsRaw)) {
    amountArr = _amtsRaw.map(String);
  }

  // fallback: total amount - GST, as one item
  if (!amountArr.length && inv.Total_Amount !== undefined) {
    amountArr = [
      String(
        Number(inv.Total_Amount) -
          Number(inv.CGSTAmount || 0) -
          Number(inv.SGSTAmount || 0) -
          Number(inv.IGSTAmount || 0)
      ),
    ];
  }

  let rows: { particulars: string; hsn_sac: string; amount: string }[] = [];
  if (
    particularsArr.length > 1 ||
    hsnArr.length > 1 ||
    amountArr.length > 1
  ) {
    const rowCount = Math.max(
      particularsArr.length,
      hsnArr.length,
      amountArr.length
    );
    for (let i = 0; i < rowCount; i++) {
      rows.push({
        particulars: particularsArr[i] ?? particularsArr[0] ?? "",
        hsn_sac: hsnArr[i] ?? hsnArr[0] ?? "",
        amount: amountArr[i] ?? amountArr[0] ?? "",
      });
    }
    // Sort rows by hsn_sac, then particulars.
    rows.sort((a, b) => {
      if (a.hsn_sac < b.hsn_sac) return -1;
      if (a.hsn_sac > b.hsn_sac) return 1;
      return a.particulars.localeCompare(b.particulars);
    });
  }

  // Fallback: legacy [{...}] object-array in Particulars string column
  if (
    !rows.length &&
    typeof inv.Particulars === "string" &&
    inv.Particulars.startsWith("[{")
  ) {
    try {
      rows = JSON.parse(inv.Particulars);
      if (!Array.isArray(rows)) rows = [];
      rows = rows.map((r: any) => ({
        particulars: r.particulars ?? "",
        hsn_sac:
          typeof r.hsn_sac !== "undefined"
            ? r.hsn_sac
            : inv.HSN_SAC ?? "",
        amount:
          typeof r.amount !== "undefined"
            ? String(r.amount)
            : "0",
      }));
    } catch (e) {
      rows = [];
    }
  }

  if (!rows.length) {
    rows = [
      {
        particulars: particularsArr[0] ?? inv.Particulars ?? "",
        hsn_sac: hsnArr[0] ?? inv.HSN_SAC ?? "",
        amount: amountArr[0] ?? "0",
      },
    ];
  }
  return rows;
}

function numberToWords(num: number): string {
  if (isNaN(num)) return "";
  if (num === 0) return "Zero";
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const g = [
    "",
    "Thousand",
    "Lakh",
    "Crore",
    "Arab",
    "Kharab",
  ];
  let s = "";
  const n = Math.floor(num);

  function _inWordsHundred(n: number) {
    let str = "";
    if (n > 99) {
      str += a[Math.floor(n / 100)] + " Hundred ";
      n = n % 100;
    }
    if (n > 19) {
      str += b[Math.floor(n / 10)] + " " + a[n % 10];
    } else {
      str += a[n];
    }
    return str.trim();
  }

  let i = 0;
  let words = [];
  let segment;

  let remaining = n;
  if (remaining === 0) return "Zero";
  while (remaining > 0 && i < g.length) {
    if (i === 0) {
      segment = remaining % 1000;
      remaining = Math.floor(remaining / 1000);
    } else {
      segment = remaining % 100;
      remaining = Math.floor(remaining / 100);
    }
    if (segment)
      words.push(_inWordsHundred(segment) + (g[i] ? " " + g[i] : ""));
    i++;
  }

  s = words.reverse().join(" ").replace(/\s+/g, " ").trim();
  s = s.charAt(0).toUpperCase() + s.slice(1);
  return s;
}

function toCurrency(val: number | string) {
  const n = Number(val) || 0;
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2 });
}

function formatDate(dateString?: string) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (String(d) === "Invalid Date") return dateString;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// --- Improved way to manage signature URLs from API ---
function getCompanySignatureUrl(company?: Company): string | undefined {
  if (!company?.signature) return undefined;
  const signature = company.signature.trim();
  if (signature.startsWith("http") || signature.startsWith("data:")) {
    return signature;
  }
  let cleanPath = signature.replace(/^\/+/, "");
  let apiBase = API_URL || "";
  if (apiBase.endsWith("/")) apiBase = apiBase.slice(0, -1);
  return `${apiBase}/${cleanPath}`;
}
function getSelectedGstRateFromInvoice(inv: InvoiceDB): number {
  let calculatedAmount = 0;
  if (
    typeof inv.Total_Amount === "number" &&
    typeof inv.SGSTAmount === "number" &&
    typeof inv.CGSTAmount === "number" &&
    typeof inv.IGSTAmount === "number"
  ) {
    calculatedAmount =
      inv.Total_Amount -
      inv.SGSTAmount -
      inv.CGSTAmount -
      inv.IGSTAmount;
  }
  let calculatedGstRate = "";
  if (
    typeof inv.Total_Amount === "number" &&
    typeof calculatedAmount === "number" &&
    calculatedAmount > 0
  ) {
    if ((inv.SGSTAmount ?? 0) > 0 && (inv.CGSTAmount ?? 0) > 0) {
      // Combined GST
      const rate =
        ((inv.SGSTAmount + inv.CGSTAmount) * 100) / calculatedAmount;
      calculatedGstRate = rate.toFixed(2);
    } else if ((inv.IGSTAmount ?? 0) > 0) {
      const rate = (inv.IGSTAmount * 100) / calculatedAmount;
      calculatedGstRate = rate.toFixed(2);
    }
  }

  // fallback to invoice GST_RATE
  return Number(calculatedGstRate);
}
function getTaxTableData(inv: InvoiceDB) {
  if (!inv) return { taxRows: [], totalTax: 0, uniqueHSN: [] };

  // Get HSN array (as per followup)
  let hsnArr: string[] = [];
  let _hsnRaw: any = (inv as any).hsn_codes !== undefined ? (inv as any).hsn_codes : inv.HSN_SAC;
  if (
    typeof _hsnRaw === "string" &&
    _hsnRaw.trim().startsWith("[") &&
    _hsnRaw.includes('"')
  ) {
    try {
      const arr = JSON.parse(_hsnRaw);
      if (Array.isArray(arr)) hsnArr = arr.map(String);
    } catch {}
  }
  if (!hsnArr.length && _hsnRaw) {
    hsnArr = [String(_hsnRaw)];
  }
  const uniqueHSN: string[] = Array.from(
    new Set(hsnArr.map((v) => String(v).trim() || "-"))
  );
  const gstrate = getSelectedGstRateFromInvoice(inv);

  // Get Amounts column
  let amountArr: number[] = [];
  let _amtsRaw: any = (inv as any).Amounts;
  if (
    typeof _amtsRaw === "string" &&
    _amtsRaw.trim().startsWith("[") &&
    _amtsRaw.includes(",")
  ) {
    try {
      const arr = JSON.parse(_amtsRaw);
      if (Array.isArray(arr)) amountArr = arr.map(Number);
    } catch {}
  } else if (Array.isArray(_amtsRaw)) {
    amountArr = _amtsRaw.map(Number);
  }

  // fallback to single amount, total minus GST
  if (!amountArr.length && inv.Total_Amount !== undefined) {
    amountArr = [
      Number(inv.Total_Amount) -
        Number(inv.SGSTAmount || 0) -
        Number(inv.CGSTAmount || 0) -
        Number(inv.IGSTAmount || 0),
    ];
  }

  // Row grouping for HSN by summing amounts
  type TaxRow = {
    hsn: string;
    taxableValue: number;
    igstRate: number;
    igstAmount: number;
    sgstRate: number;
    sgstAmount: number;
    cgstRate: number;
    cgstAmount: number;
    totalTax: number;
  };

  let rowMap: { [hsn: string]: TaxRow } = {};
  if (hsnArr.length > 0 && amountArr.length > 0) {
    for (let i = 0; i < Math.max(hsnArr.length, amountArr.length); i++) {
      let hsn = hsnArr[i] ?? hsnArr[0] ?? "-";
      let amount = amountArr[i] ?? amountArr[0] ?? 0;
      let igstRate = gstrate && Number(inv.IGSTAmount) > 0 ? Number(gstrate) : 0;
      let sgstRate = gstrate && Number(inv.SGSTAmount) > 0 ? Number(gstrate) / 2 : 0;
      let cgstRate = gstrate && Number(inv.CGSTAmount) > 0 ? Number(gstrate) / 2 : 0;
      let igstAmount = igstRate ? (igstRate * amount) / 100 : 0;
      let sgstAmount = sgstRate ? (sgstRate * amount) / 100 : 0;
      let cgstAmount = cgstRate ? (cgstRate * amount) / 100 : 0;
      let totalTax = igstAmount + sgstAmount + cgstAmount;
      if (rowMap[hsn]) {
        rowMap[hsn].taxableValue += amount;
        rowMap[hsn].igstAmount += igstAmount;
        rowMap[hsn].sgstAmount += sgstAmount;
        rowMap[hsn].cgstAmount += cgstAmount;
        rowMap[hsn].totalTax += totalTax;
      } else {
        rowMap[hsn] = {
          hsn,
          taxableValue: amount,
          igstRate,
          igstAmount,
          sgstRate,
          sgstAmount,
          cgstRate,
          cgstAmount,
          totalTax,
        };
      }
    }
  } else {
    let hsn = String(inv.HSN_SAC).trim() || "-";
    let taxableValue =
      Number(inv.Total_Amount || 0) -
      Number(inv.SGSTAmount || 0) -
      Number(inv.CGSTAmount || 0) -
      Number(inv.IGSTAmount || 0);
    let totalTax =
      Number(inv.SGSTAmount || 0) +
      Number(inv.CGSTAmount || 0) +
      Number(inv.IGSTAmount || 0);

    const igstRate = gstrate;
    const sgstRate = gstrate / 2;
    rowMap[hsn] = {
      hsn,
      taxableValue,
      igstRate,
      igstAmount: Number(inv.IGSTAmount),
      sgstRate,
      sgstAmount: Number(inv.SGSTAmount),
      cgstRate: sgstRate,
      cgstAmount: Number(inv.CGSTAmount),
      totalTax,
    };
  }

  const result = Object.values(rowMap);

  const totalTax = result.reduce((sum, row) => sum + row.totalTax, 0);
  return {
    taxRows: result,
    totalTax,
    uniqueHSN,
  };
}

// Dummy handlers for additional actions
function handleSendReminder(invoice: InvoiceDB) {
  alert(`Send payment reminder for invoice #${invoice.InvoiceNo}`);
}
function handleSendEmail(invoice: InvoiceDB) {
  alert(`Send invoice by email for invoice #${invoice.InvoiceNo}`);
}
function handleSendWhatsapp(invoice: InvoiceDB) {
  alert(`Send invoice by WhatsApp for invoice #${invoice.InvoiceNo}`);
}
function handleEditInvoice(invoice: InvoiceDB) {
  // This should navigate to edit, for now simulate
  window.location.href = `/editinvoice?id=${invoice.id}`;
}
async function handleCancelInvoice(
  invoice: InvoiceDB,
  fetchAllData: () => void,
  setErrorMsg: (msg: string) => void,
  setUpdateLoadingId: (id: number | string | null) => void
) {
  if (
    !window.confirm(
      "Are you sure you want to cancel this invoice? This action will mark it as cancelled."
    )
  )
    return;
  setUpdateLoadingId(invoice.id!);
  setErrorMsg("");
  try {
    await axios.put(`${API_URL}/auth/invoice/cancel/${invoice.id}`);
    fetchAllData();
  } catch (err) {
    setErrorMsg("Failed to cancel the invoice.");
  } finally {
    setUpdateLoadingId(null);
  }
}

const Invoice: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [allInvoices, setAllInvoices] = useState<InvoiceDB[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [viewingInvoice, setViewingInvoice] = useState<InvoiceDB | null>(
    null
  );

  const [updateLoadingId, setUpdateLoadingId] = useState<number | string | null>(
    null
  );
  const [deleteLoadingId, setDeleteLoadingId] = useState<
    number | string | null
  >(null);

  // Store company signature URLs mapped by company id for easy use in UI
  const [companySignatureUrls, setCompanySignatureUrls] = useState<{
    [key: string]: string;
  }>({});

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line
  }, []);

  const handlePrint = () => {
    const invoicePrintElem = document.getElementById("invoice-print");
    if (!invoicePrintElem) return;
    const printContents = invoicePrintElem.innerHTML;
    const printWindow = window.open("", "", "width=900,height=700");
    if (printWindow) {
      printWindow.document.write(`
        <html>
        <head>
          <title>Invoice</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 4px 7px !important; background: #f7fafb; color: #222; font-size: 11.5px !important; margin: 0; line-height: 1.25; max-width: 830px; width: 100vw; box-sizing: border-box; }
            .text-xs { font-size: 10px !important; }
            .font-semibold { font-weight: 600; }
            .font-bold { font-weight: 700; }
            .font-extrabold { font-weight: 800; }
            .text-lg { font-size: 14px !important; }
            .text-xl { font-size: 15px !important; }
            .text-green-700 { color: #15803d; }
            .text-blue-900 { color: #1e3a8a;}
            .text-blue-800 { color: #1e40af;}
            .text-blue-700 { color: #1d4ed8;}
            .text-gray-700 { color: #374151;}
            .text-gray-800 { color: #1f2937;}
            .italic { font-style: italic; }
            .bg-blue-50 { background: #eff6ff; }
            .bg-blue-100 { background: #dbeafe; }
            .bg-gradient-to-tr {
              background: linear-gradient(135deg, #fff 0%, #eaeffe 40%, #f0f7ff 100%);
            }
            .bg-gradient-to-t { background: linear-gradient(0deg, #eaeffe 0%, #e8f5fe 100%);}
            .bg-gradient-to-l { background: linear-gradient(270deg, #ffe4ef 0%, #fff 100%);}
            .bg-gray-50 { background: #f9fafb;}
            .bg-white { background: #fff;}
            .mb-1 { margin-bottom: 2px !important;}
            .ml-8 { margin-left: 16px !important;}
            .mb-2 { margin-bottom: 6px !important;}
            .mb-4 { margin-bottom: 10px !important;}
            .mb-5 { margin-bottom: 11px !important;}
            .mb-6 { margin-bottom: 13px !important;}
            .mt-1 { margin-top: 2px !important;}
            .mt-2 { margin-top: 4px !important;}
            .mt-4 { margin-top: 9px !important;}
            .mt-5 { margin-top: 10px !important;}
            .rounded-2xl { border-radius: 0.65rem;}
            .rounded-xl { border-radius: 0.4rem;}
            .rounded-lg { border-radius: 0.25rem;}
            .rounded-bl-lg { border-bottom-left-radius: 0.25rem;}
            .rounded-br-lg { border-bottom-right-radius: 0.25rem;}
            .border { border: 1px solid #cbd5e1;}
            .border-blue-100 { border-color: #dbeafe;}
            .border-blue-200 { border-color: #bfdbfe;}
            .border-blue-300 { border-color: #93c5fd;}
            .border-gray-200 { border-color: #e5e7eb;}
            .shadow, .shadow-lg, .shadow-2xl, .shadow-xl { box-shadow: none !important;}
            .table-title { background: #2062b8; color: #fff; font-weight: 600;}
            .invoice-total { background: #e9fbe8 !important; font-size: 13px !important;}
            .w-full { width: 100%;}
            .w-1\\/2 { width: 50%; }
            .align-top { vertical-align: top;}
            .text-right { text-align: right;}
            .text-center { text-align: center;}
            .flex { display: flex;}
            .flex-col { flex-direction: column;}
            .flex-row { flex-direction: row;}
            .items-center { align-items: center;}
            .items-end { align-items: flex-end;}
            .text-end { text-align: end;}
            .justify-between { justify-content: space-between;}
            .justify-end { justify-content: flex-end;}
            .justify-center { justify-content: center;}
            .gap-5 { gap: 10px;}
            .gap-6 { gap: 15px;}
            .overflow-auto { overflow: auto;}
            .overflow-x-auto { overflow-x: auto;}
            .h-12 { height: 36px !important;}
            .p-2 {padding:5px!important;} 
            .p-3 { padding: 7px !important;}
            .p-4 { padding: 7px !important;}
            .p-6 { padding: 14px !important;}
            .py-1 { padding-top: 2px !important; padding-bottom: 2px !important;}
            .py-2 { padding-top: 5px !important; padding-bottom: 5px !important;}
            .py-3 { padding-top: 7px !important; padding-bottom: 7px !important;}
            .px-2 { padding-left: 5px !important; padding-right: 5px !important;}
            .px-3 { padding-left: 8px !important; padding-right: 8px !important;}
            .px-6 { padding-left: 14px !important; padding-right: 14px !important;}
            .border-collapse { border-collapse: collapse;}
            .border-l-0 { border-left: 0;}
            .border-r { border-right: 1px solid #bfdbfe;}
            #invoice-print { max-height: 99vh; overflow: hidden; }
            /* Added styling for Tax Invoice heading */
            .tax-invoice-heading {
              text-align: center;
              font-size: 22px;
              font-weight: 800;
              letter-spacing: 1px;
              color: #1e3a8a;
              padding-top: 24px;
              padding-bottom: 18px;
            }
            @media print {
              html, body {
                width: 100vw;
                max-width: 856px !important;
                margin: 0 auto !important;
                padding: 0 !important;
                overflow: hidden !important;
                font-size: 11.5px !important;
              }
              #invoice-print {
                padding: 0 6px !important;
                margin: 0 !important;
                width: 100% !important;
                max-width: 848px !important;
                max-height: 99vh !important;
                overflow: hidden !important;
              }
              .shadow, .shadow-lg, .shadow-xl, .shadow-2xl { box-shadow: none !important;}
              .bg-gradient-to-tr, .bg-gradient-to-t, .bg-gradient-to-l, .bg-gray-50, .bg-blue-50, .bg-blue-100, .bg-white { background: #fff !important; }
              tr, td, th, table, div, span, section {
                page-break-inside: avoid !important;
                page-break-after: auto !important;
                page-break-before: auto !important;
              }
              table, tbody, thead, tfoot, tr, td, th {
                margin: 0 !important;
                padding: 0 !important;
              }
              .tax-invoice-heading {
                text-align: center;
                font-size: 22px;
                font-weight: 800;
                letter-spacing: 1px;
                color: #1e3a8a;
                padding-top: 24px;
                padding-bottom: 18px;
              }
            }
          </style>
        </head>
        <body>
          ${printContents.replace(
            /^(\s*)<div[^>]*id="?invoice-print"?[^>]*>/i,
            ""
          ).replace(/<\/div>\s*$/, "")}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 350);
      };
    }
  };

  function fetchAllData() {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/auth/companies`),
      axios.get(`${API_URL}/auth/customers`),
      axios.get(`${API_URL}/auth/invoices`),
    ])
      .then(([comp, cust, invs]) => {
        const compArray: Company[] = Array.isArray(comp.data)
          ? comp.data
          : [];
        setCompanies(compArray);
        setCustomers(Array.isArray(cust.data) ? cust.data : []);
        setAllInvoices(Array.isArray(invs.data) ? invs.data : []);

        // Fill signature URLs for all companies and store to state
        const signatureMap: { [key: string]: string } = {};
        compArray.forEach((c) => {
          const url = getCompanySignatureUrl(c);
          if (url) signatureMap[String(c.id)] = url;
        });
        setCompanySignatureUrls(signatureMap);
      })
      .catch(() => setErrorMsg("Failed to fetch data"))
      .finally(() => setLoading(false));
  }

  function handleViewModal(inv: InvoiceDB) {
    setViewingInvoice(inv);
  }

  function handleCloseModal() {
    setViewingInvoice(null);
  }

  async function handlePaidStatusUpdate(invId: number | string, paid: boolean) {
    setUpdateLoadingId(invId);
    setErrorMsg("");
    try {
      await axios.put(`${API_URL}/auth/invoiceremark/${invId}`, {
        Paid: paid,
      });
      await fetchAllData();
    } catch (err) {
      setErrorMsg("Failed to update Paid status.");
    } finally {
      setUpdateLoadingId(null);
    }
  }

  async function handleDeleteInvoice(invId: number | string) {
    if (
      !window.confirm(
        "Are you sure you want to delete this invoice? This action cannot be undone."
      )
    )
      return;

    setDeleteLoadingId(invId);
    setErrorMsg("");
    try {
      await axios.delete(`${API_URL}/auth/invoice/${invId}`);
      await fetchAllData();
    } catch (err) {
      setErrorMsg("Failed to delete the invoice.");
    } finally {
      setDeleteLoadingId(null);
    }
  }

  function getPreviewRows() {
    if (!viewingInvoice) return [];
    return getInvoiceRowsFromDB(viewingInvoice);
  }

  function getInvoicePreviewData() {
    // Build data needed for preview without crashing
    if (!viewingInvoice) {
      return {
        selectedGstRate: 0,
        cgstAmount: 0,
        sgstAmount: 0,
        igstAmount: 0,
        totalAmount: 0,
        companyId: "",
      };
    }
    const inv = viewingInvoice;
    const selectedGstRate = getSelectedGstRateFromInvoice(inv);
    return {
      selectedGstRate,
      cgstAmount: Number(inv.CGSTAmount || 0),
      sgstAmount: Number(inv.SGSTAmount || 0),
      igstAmount: Number(inv.IGSTAmount || 0),
      totalAmount: Number(inv.Total_Amount || 0),
      companyId: inv.Company_id,
    };
  }

  const taxTableData = getTaxTableData(viewingInvoice || ({} as InvoiceDB));
  const invoicePreviewData = getInvoicePreviewData();

  const amountInWords = numberToWords(Number(invoicePreviewData.totalAmount));
  const taxAmountInWords = numberToWords(
    Number(invoicePreviewData.cgstAmount) +
      Number(invoicePreviewData.sgstAmount) +
      Number(invoicePreviewData.igstAmount)
  );

  return (
    <div className="max-w-7xl mx-auto h-screen flex flex-col bg-gradient-to-b from-blue-50 via-white to-gray-100">
      {/* Header section */}
      <div className="flex justify-between items-center gap-4 mt-6 mb-8 px-2 md:px-4">
        <div>
          <h2 className="text-3xl font-extrabold text-blue-800 text-center md:text-left tracking-tight">
            All Invoices
          </h2>
          <p className="text-left text-gray-500 mt-1 mb-0 text-md">
            Browse, view, update paid status, or delete invoices.
          </p>
        </div>
        <div>
          <button
            className="bg-gradient-to-r from-blue-700 to-blue-500 text-white hover:from-blue-500 hover:to-blue-400 px-6 py-2 rounded-full text-md font-semibold shadow transition duration-150"
            onClick={() => (window.location.href = "/InvoiceCreator")}
          >
            Create Invoice
          </button>
        </div>
      </div>

      {errorMsg && (
        <div className="mb-4 p-2 rounded border text-center shadow bg-red-50 text-red-700 border-red-200 mx-2 md:mx-4">
          {errorMsg}
        </div>
      )}

      {/* Main Table Area - scrollable if many invoices */}
      <div className="flex-1 min-h-0 px-2 md:px-4 pb-6">
        <div className="h-full rounded-3xl overflow-auto bg-gradient-to-br from-white via-blue-50 to-slate-50 shadow-2xl border border-blue-200 p-6">
          <table className="min-w-[920px] w-full text-sm rounded-xl shadow table-auto border-separate border-spacing-y-2">
            <thead>
              <tr className="bg-gradient-to-l from-blue-600 to-blue-400 text-white text-base rounded-xl">
                <th className="px-3 py-2 rounded-l-xl">#</th>
                <th className="px-3 py-2">Invoice No</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Company</th>
                <th className="px-3 py-2">Buyer</th>
                <th className="px-3 py-2">Amount</th>
                <th className="px-3 py-2">Paid</th>
                <th className="px-3 py-2">Actions</th>
                <th className="rounded-r-xl"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={9}
                    className="py-10 text-center text-gray-400 text-lg"
                  >
                    Loading...
                  </td>
                </tr>
              ) : allInvoices.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="py-10 text-center text-gray-400 text-lg"
                  >
                    No invoices found.
                  </td>
                </tr>
              ) : (
                allInvoices.map((inv, idx) => {
                  const company = companies.find(
                    (c) => String(c.id) === String(inv.Company_id)
                  );
                  const customer = customers.find(
                    (c) => String(c.id) === String(inv.Customer_id)
                  );
                  return (
                    <tr
                      key={inv.id}
                      className="transition-all hover:ring-4 hover:ring-blue-200/30 hover:shadow-lg rounded-lg even:bg-slate-50"
                    >
                      <td className="py-1 px-3 font-medium text-blue-700 select-none">
                        {idx + 1}
                      </td>
                      <td className="py-1 px-3 font-semibold">
                        {inv.InvoiceNo}
                      </td>
                      <td className="py-1 px-3">{formatDate(inv.Date)}</td>
                      <td className="py-1 px-3">
                        {company?.company_name || "-"}
                      </td>
                      <td className="py-1 px-3">
                        {customer?.company_name || "-"}
                      </td>
                      <td className="py-1 px-3">
                        ₹ {toCurrency(inv.Total_Amount)}
                      </td>
                      <td className="py-1 px-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <label className="inline-flex items-center cursor-pointer select-none">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={!!inv.paid}
                              disabled={updateLoadingId === inv.id}
                              onChange={async (e) => {
                                await handlePaidStatusUpdate(
                                  inv.id!,
                                  e.target.checked
                                );
                              }}
                            />
                            <div
                              className={`
                                w-11 h-6 bg-gray-300 peer-checked:bg-green-500
                                rounded-full shadow-inner relative transition-colors
                                duration-200 border border-gray-300 peer-checked:border-green-600
                              `}
                            >
                              <div
                                className={`
                                  absolute left-1 top-1 bg-white h-4 w-4 rounded-full shadow 
                                  transition-transform duration-200
                                  ${inv.paid === 1 ? "translate-x-5" : ""}
                                `}
                              ></div>
                            </div>
                            <span
                              className={`ml-3 text-sm font-bold transition-colors duration-200 ${
                                inv.paid === 1
                                  ? "text-green-700"
                                  : "text-yellow-700"
                              }`}
                            >
                              {inv.paid === 1 ? (
                                <span className="inline-flex items-center gap-1">
                                  <svg
                                    className="w-4 h-4 text-green-600"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={3}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                  Paid
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1">
                                  <svg
                                    className="w-4 h-4 text-yellow-600"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={3}
                                      d="M18.364 5.636l-1.414-1.414-7.778 7.778-2.121-2.121-1.415 1.415 3.536 3.535L18.364 5.636z"
                                    />
                                  </svg>
                                  Unpaid
                                </span>
                              )}
                            </span>
                          </label>
                          {updateLoadingId === inv.id && (
                            <span className="ml-2 text-blue-500 text-xs animate-pulse">
                              Updating...
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-1 px-3 text-center flex gap-2 flex-wrap justify-center">
                        <button
                          className="px-3 py-1.5 bg-gradient-to-t from-blue-500 to-blue-300 rounded-lg text-white text-xs font-bold hover:from-blue-700 hover:to-blue-500 shadow transition"
                          title="View Invoice"
                          onClick={() => handleViewModal(inv)}
                        >
                          View
                        </button>
                        <button
                          className="px-3 py-1.5 bg-gradient-to-t from-orange-400 to-orange-300 rounded-lg text-white text-xs font-bold hover:from-orange-700 hover:to-orange-500 shadow transition"
                          title="Edit Invoice"
                          onClick={() => handleEditInvoice(inv)}
                          disabled={!!inv.cancelled}
                        >
                          Edit
                        </button>
                        <button
                          className="px-3 py-1.5 bg-gradient-to-t from-yellow-500 to-yellow-300 rounded-lg text-white text-xs font-bold hover:from-yellow-600 hover:to-yellow-400 shadow transition"
                          title="Send Payment Reminder"
                          onClick={() => handleSendReminder(inv)}
                          disabled={!!inv.paid || !!inv.cancelled}
                        >
                          Reminder
                        </button>
                        <button
                          className="px-3 py-1.5 bg-gradient-to-t from-green-500 to-green-300 rounded-lg text-white text-xs font-bold hover:from-green-700 hover:to-green-400 shadow transition"
                          title="Send Invoice by Email"
                          onClick={() => handleSendEmail(inv)}
                          disabled={!!inv.cancelled}
                        >
                          Email
                        </button>
                        <button
                          className="px-3 py-1.5 bg-gradient-to-t from-teal-500 to-teal-300 rounded-lg text-white text-xs font-bold hover:from-teal-700 hover:to-teal-400 shadow transition"
                          title="Send Invoice via WhatsApp"
                          onClick={() => handleSendWhatsapp(inv)}
                          disabled={!!inv.cancelled}
                        >
                          WhatsApp
                        </button>
                        <button
                          className={`px-3 py-1.5 bg-gradient-to-t from-gray-500 to-gray-300 rounded-lg text-white text-xs font-bold hover:from-gray-700 hover:to-gray-500 shadow transition ${
                            !!inv.cancelled
                              ? "opacity-40 cursor-not-allowed"
                              : ""
                          }`}
                          title="Cancel Invoice"
                          onClick={() =>
                            handleCancelInvoice(
                              inv,
                              fetchAllData,
                              setErrorMsg,
                              setUpdateLoadingId
                            )
                          }
                          disabled={!!inv.cancelled || !!inv.paid}
                        >
                          Cancel
                        </button>
                        <button
                          className="px-3 py-1.5 bg-gradient-to-t from-red-500 to-red-400 rounded-lg text-white text-xs font-bold hover:from-red-700 hover:to-red-500 shadow transition"
                          title="Delete Invoice"
                          onClick={() => handleDeleteInvoice(inv.id!)}
                          disabled={deleteLoadingId === inv.id}
                        >
                          {deleteLoadingId === inv.id
                            ? "Deleting..."
                            : "Delete"}
                        </button>
                      </td>
                      <td>
                        {!!inv.cancelled && (
                          <span className="rounded bg-red-100 text-red-700 font-bold text-xs px-2 py-1">
                            Cancelled
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoice view modal with full h-screen usage */}
      {viewingInvoice && (
        <div className="fixed inset-0 z-50 flex flex-col h-screen w-screen">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40"></div>
          {/* Modal */}
          <div className="relative flex flex-col flex-1 justify-center items-center h-full w-full py-7 px-2">
            <div className="bg-gradient-to-tr from-white to-blue-50 w-full max-w-[900px] h-full max-h-[97vh] flex flex-col overflow-hidden rounded-2xl shadow-2xl border border-blue-200">
              <div className="flex justify-end gap-5 py-3 px-4 flex-shrink-0 bg-gradient-to-r from-blue-200/20 via-blue-100/5 to-white rounded-t-2xl border-b border-blue-100">
                <button
                  onClick={() => handlePrint()}
                  className="bg-gradient-to-r from-blue-600 to-blue-400 text-white px-3 py-2 rounded-xl shadow hover:from-blue-400 hover:to-blue-200 transition text-[13px]"
                >
                  Print
                </button>
                <button
                  onClick={handleCloseModal}
                  className="bg-gray-500 hover:bg-gray-800 text-white px-3 py-2 rounded-xl shadow transition text-[13px]"
                >
                  Close
                </button>
              </div>
              {/* Modal Content - Scrollable, fit rest height */}
              <div
                id="invoice-print"
                className="p-2 text-[11.5px] text-gray-800 overflow-scroll"
              >
                {/* Tax Invoice Heading */}
                <div
                  className="text-center py-4"
                  style={{
                    fontSize: "22px",
                    fontWeight: 800,
                    letterSpacing: "1px",
                    color: "#1e3a8a",
                    paddingTop: "24px",
                    paddingBottom: "18px",
                  }}
                >
                  TAX INVOICE
                </div>
                <table className="w-full border border-blue-300 border-collapse rounded-2xl shadow mb-2">
                  <tbody>
                    <tr className="invoice__header">
                      <td className="border p-2 w-1/2 align-top">
                        <div
                          className="mb-1 text-xl text-blue-900 font-extrabold tracking-wide"
                          style={{
                            fontSize: "14.5px",
                            lineHeight: "1.2",
                          }}
                        >
                          {
                            companies.find(
                              (c) =>
                                String(c.id) ===
                                String(viewingInvoice.Company_id)
                            )?.company_name
                          }
                        </div>
                        <div
                          className="mb-1 text-gray-700"
                          style={{ fontSize: "11.5px" }}
                        >
                          <b>Address:</b>{" "}
                          {
                            companies.find(
                              (c) =>
                                String(c.id) ===
                                String(viewingInvoice.Company_id)
                            )?.address
                          }
                        </div>
                        <div
                          className="mb-1 text-gray-700"
                          style={{ fontSize: "11.5px" }}
                        >
                          <b>GSTIN:</b>{" "}
                          {
                            companies.find(
                              (c) =>
                                String(c.id) ===
                                String(viewingInvoice.Company_id)
                            )?.gst_number
                          }
                        </div>
                        <div
                          className="mb-1 text-gray-700"
                          style={{ fontSize: "11.5px" }}
                        >
                          <b>State:</b>{" "}
                          {
                            companies.find(
                              (c) =>
                                String(c.id) ===
                                String(viewingInvoice.Company_id)
                            )?.state
                          }
                        </div>
                        <div
                          className="mb-1 text-gray-700"
                          style={{ fontSize: "11.5px" }}
                        >
                          <b>CIN:</b>{" "}
                          {
                            companies.find(
                              (c) =>
                                String(c.id) ===
                                String(viewingInvoice.Company_id)
                            )?.cin
                          }
                        </div>
                        <div
                          className="mb-1 text-gray-700"
                          style={{ fontSize: "11.5px" }}
                        >
                          <b>Email:</b>{" "}
                          {
                            companies.find(
                              (c) =>
                                String(c.id) ===
                                String(viewingInvoice.Company_id)
                            )?.email
                          }
                        </div>
                      </td>
                      <td className="border p-2 align-top bg-gradient-to-t from-blue-50 to-blue-100 flex flex-row">
                        <div
                          className="mb-1 flex flex-col"
                          style={{ fontSize: "12px", minWidth: "135px" }}
                        >
                          <b>Invoice No:</b>{" "}
                          <span className="text-blue-700">
                            {viewingInvoice.InvoiceNo}
                          </span>
                        </div>
                        <div
                          className="mb-1 flex flex-col ml-8"
                          style={{ fontSize: "12px", minWidth: "110px" }}
                        >
                          <b>Dated:</b>{" "}
                          <span className="text-blue-700">
                            {viewingInvoice.Date ? new Date(viewingInvoice.Date).toLocaleDateString("en-GB") : ""}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="border p-2 align-top bg-gradient-to-l from-pink-50 to-white/90" style={{ fontSize: '11.5px' }}>
                        <b className="text-blue-800">Buyer (Bill to)</b>
                        <div className="" />
                        <div className="mb-1 mt-1">
                          <b>
                            {
                              customers.find(
                                (c) => String(c.id) === String(viewingInvoice.Customer_id)
                              )?.company_name
                            }
                          </b>
                        </div>
                        <div className="mb-1"><b>City:</b>{" "}
                          {
                            customers.find(
                              (c) => String(c.id) === String(viewingInvoice.Customer_id)
                            )?.city
                          }
                        </div>
                        <div className="mb-1"><b>State:</b>{" "}
                          {
                            customers.find(
                              (c) => String(c.id) === String(viewingInvoice.Customer_id)
                            )?.state
                          }
                        </div>
                        <div className="mb-1"><b>GSTIN:</b>{" "}
                          {
                            customers.find(
                              (c) => String(c.id) ===String(viewingInvoice.Customer_id)
                            )?.gst_number
                          }
                        </div>
                      </td>
                      <td className="border p-2 bg-white"></td>
                    </tr>
                  </tbody>
                </table>
                {/* ...rest unchanged for brevity */}
                {/* Render safely from getPreviewRows */}
                <table
                  className="w-full border border-black border-collapse mt-[2px] mb-2 rounded-lg"
                  style={{
                    fontSize: "11.5px",
                    borderWidth: "1.2px",
                  }}
                >
                  <thead>
                    <tr style={{ height: "22px", fontSize: "11.7px" }}>
                      <th
                        className="border border-black"
                        style={{
                          padding: "3px 6px",
                          textAlign: "center",
                          fontWeight: 600,
                        }}
                      >
                        Particulars
                      </th>
                      <th
                        className="border border-black"
                        style={{
                          padding: "3px 6px",
                          textAlign: "center",
                          fontWeight: 600,
                          width: "90px",
                        }}
                      >
                        HSN/SAC
                      </th>
                      <th
                        className="border border-black"
                        style={{
                          padding: "3px 6px",
                          textAlign: "center",
                          fontWeight: 600,
                          width: "110px",
                        }}
                      >
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPreviewRows().map((r, i) => (
                      <tr key={i}>
                        <td
                          className="border border-black align-top"
                          style={{
                            padding: "7px 10px 24px 7px",
                            fontSize: "12.4px",
                            minHeight: "90px",
                          }}
                        >
                          <div>
                            <div style={{ whiteSpace: "pre-line" }}>
                              {r.particulars}
                            </div>
                            {i === 0 && (
                              <div
                                style={{
                                  marginTop: 24,
                                  fontWeight: 700,
                                  letterSpacing: 1,
                                  fontFamily: "inherit",
                                  fontSize: "12px",
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                {/* GST sublabels if needed */}
                              </div>
                            )}
                          </div>
                        </td>
                        <td
                          className="border border-black align-top"
                          style={{
                            padding: "7px 8px",
                            fontSize: "12.6px",
                            textAlign: "center",
                            minWidth: "64px",
                          }}
                        >
                          {r.hsn_sac}
                        </td>
                        <td
                          className="border border-black align-top text-right"
                          style={{
                            padding: "7px 8px",
                            fontSize: "12.6px",
                            fontFamily: "inherit",
                            minWidth: "84px",
                          }}
                        >
                          {parseFloat(r.amount).toLocaleString("en-IN")}
                        </td>
                      </tr>
                    ))}
                    {/* CGST/SGST/IGST Rows */}
                    {Number(invoicePreviewData.sgstAmount) > 0 && (
                      <tr>
                        <td
                          className="border-t-0 border-l border-b-0 border-black text-end"
                          style={{ padding: 0 }}
                        >
                          <span
                            style={{
                              marginLeft: "auto",
                              marginBottom: "-2px",
                              lineHeight: 1.33,
                              color: "#222",
                              fontWeight: 600,
                            }}
                          >
                            CGST (
                            {Number(invoicePreviewData.selectedGstRate) / 2}
                            %)
                          </span>
                        </td>
                        <td
                          className="border-t-0 border-b-0 border-black"
                          style={{ padding: 0 }}
                        ></td>
                        <td
                          className="border border-black text-right"
                          style={{
                            padding: "5.3px 8px",
                            fontSize: "12.7px",
                            fontWeight: 500,
                          }}
                        >
                          {Number(
                            invoicePreviewData.cgstAmount
                          ).toLocaleString("en-IN")}
                        </td>
                      </tr>
                    )}
                    {Number(invoicePreviewData.sgstAmount) > 0 && (
                      <tr>
                        <td
                          className="border-t-0 border-l border-b-0 border-black text-end"
                          style={{ padding: 0 }}
                        >
                          <span
                            style={{
                              marginLeft: "auto",
                              marginBottom: "-2px",
                              lineHeight: 1.33,
                              color: "#222",
                              fontWeight: 600,
                            }}
                          >
                            SGST(
                            {Number(invoicePreviewData.selectedGstRate) / 2}
                            %)
                          </span>
                        </td>
                        <td
                          className="border-t-0 border-b-0 border-black"
                          style={{ padding: 0 }}
                        ></td>
                        <td
                          className="border border-black text-right"
                          style={{
                            padding: "5.3px 8px",
                            fontSize: "12.7px",
                            fontWeight: 500,
                          }}
                        >
                          {Number(
                            invoicePreviewData.sgstAmount
                          ).toLocaleString("en-IN")}
                        </td>
                      </tr>
                    )}
                    {Number(invoicePreviewData.igstAmount) > 0 && (
                      <tr>
                        <td
                          className="border-t-0 border-l border-b-0 border-black text-end"
                          style={{ padding: 0 }}
                        >
                          <span
                            style={{
                              marginLeft: "auto",
                              marginBottom: "-2px",
                              lineHeight: 1.33,
                              color: "#222",
                              fontWeight: 600,
                            }}
                          >
                            IGST(
                            {Number(invoicePreviewData.selectedGstRate)}%)
                          </span>
                        </td>
                        <td
                          className="border-t-0 border-b-0 border-black"
                          style={{ padding: 0 }}
                        ></td>
                        <td
                          className="border border-black text-right"
                          style={{
                            padding: "5.3px 8px",
                            fontSize: "12.7px",
                            fontWeight: 500,
                          }}
                        >
                          {Number(
                            invoicePreviewData.igstAmount
                          ).toLocaleString("en-IN")}
                        </td>
                      </tr>
                    )}
                    <tr className="font-bold bg-green-50 invoice-total">
                      <td className="border p-2 text-right" colSpan={2}>
                        Total
                      </td>
                      <td className="border p-2 text-right text-green-700 text-[16px] font-bold">
                        ₹ {invoicePreviewData.totalAmount}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="mt-2 mb-1">
                  {/* Amount in Words */}
                  <div className="w-full border border-gray-200 rounded-lg bg-gray-50 px-2 py-2 mb-2 flex flex-col">
                    <span
                      className="text-lg font-bold text-blue-900 mb-1"
                      style={{ fontSize: "12.5px" }}
                    >
                      Amount Chargeable (in words)
                    </span>
                    <span className="text-[11.5px] text-gray-700 font-mono">
                      {amountInWords} /-
                    </span>
                  </div>
                  {/* Tax Details Table */}
                  <div className="w-full border border-blue-200 rounded-xl bg-white px-0 sm:px-1 py-2 mb-2 shadow-lg">
                    <div className="flex items-center border-b border-blue-100 pb-1 px-2">
                      <span
                        className="text-blue-900 text-lg font-semibold tracking-wide"
                        style={{ fontSize: "12.2px" }}
                      >
                        Tax Details
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse mt-1">
                        <thead>
                          <tr className="bg-blue-50 text-blue-900 text-[11px]">
                            <th className="border border-blue-200 px-2 py-1 text-left font-semibold align-middle">
                              HSN/SAC
                            </th>
                            <th className="border border-blue-200 px-2 py-1 text-right font-semibold align-middle">
                              Taxable Value (₹)
                            </th>
                            <th className="border border-blue-200 px-1 py-0" colSpan={2}>
                              <div className="flex flex-col text-center">
                                <span className="font-semibold" style={{ fontSize: "11px" }}>
                                  IGST
                                </span>
                                <div className="flex justify-between w-full border-t border-blue-100 mt-1">
                                  <span className="flex-1 border-r border-blue-100 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>
                                    Rate (%)
                                  </span>
                                  <span className="flex-1 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>
                                    Amount (₹)
                                  </span>
                                </div>
                              </div>
                            </th>
                            <th className="border border-blue-200 px-1 py-0" colSpan={2}>
                              <div className="flex flex-col text-center">
                                <span className="font-semibold" style={{ fontSize: "11px" }}>
                                  SGST
                                </span>
                                <div className="flex justify-between  w-full border-t border-blue-100 mt-1">
                                  <span className="flex-1 border-r border-blue-100 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>
                                    Rate (%)
                                  </span>
                                  <span className="flex-1 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>
                                    Amount (₹)
                                  </span>
                                </div>
                              </div>
                            </th>
                            <th className="border border-blue-200 px-1 py-0" colSpan={2}>
                              <div className="flex flex-col text-center">
                                <span className="font-semibold" style={{ fontSize: "11px" }}>
                                  CGST
                                </span>
                                <div className="flex justify-between w-full border-t border-blue-100 mt-1">
                                  <span className="flex-1 border-r border-blue-100 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>
                                    Rate (%)
                                  </span>
                                  <span className="flex-1 py-1 text-xs font-normal" style={{ fontSize: "9.5px" }}>
                                    Amount (₹)
                                  </span>
                                </div>
                              </div>
                            </th>
                            <th className="border border-blue-200 px-2 py-1 text-right font-semibold align-middle">
                              Total Tax
                              <br />
                              Amount (₹)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const { taxRows } = taxTableData;
                            if (!taxRows.length) {
                              // Demo Row
                              return (
                                <tr className="even:bg-blue-50 text-[11px]">
                                  <td className="border border-blue-100 px-2 py-1 align-middle"></td>
                                  <td className="border border-blue-100 px-2 py-1 text-right align-middle"></td>
                                  <td className="border border-blue-100 px-1 py-1 text-center align-middle"></td>
                                  <td className="border border-blue-100 px-1 py-1 text-right align-middle"></td>
                                  <td className="border border-blue-100 px-1 py-1 text-center align-middle"></td>
                                  <td className="border border-blue-100 px-1 py-1 text-right align-middle"></td>
                                  <td className="border border-blue-100 px-1 py-1 text-center align-middle"></td>
                                  <td className="border border-blue-100 px-1 py-1 text-right align-middle"></td>
                                  <td className="border border-blue-100 px-2 py-1 text-right align-middle font-semibold"></td>
                                </tr>
                              );
                            }
                            return taxRows.map((row, idx) => (
                              <tr key={row.hsn + idx} className="even:bg-blue-50 text-[11px]">
                                {/* HSN */}
                                <td className="border border-blue-100 px-2 py-1 align-middle">{row.hsn}</td>
                                {/* Taxable Value */}
                                <td className="border border-blue-100 px-2 py-1 text-right align-middle">
                                  {row.taxableValue}
                                </td>
                                {/* IGST Rate and Amount */}
                                <td className="border border-blue-100 px-1 py-1 text-center align-middle">
                                  {row.igstRate && row.igstAmount ? `${row.igstRate}%` : "-"}
                                </td>
                                <td className="border border-blue-100 px-1 py-1 text-right align-middle">
                                  {row.igstAmount ? row.igstAmount : "-"}
                                </td>
                                {/* SGST/UTGST Rate and Amount */}
                                <td className="border border-blue-100 px-1 py-1 text-center align-middle">
                                  {row.sgstRate && row.sgstAmount
                                    ? `${row.sgstRate}%`
                                    : "-"}
                                </td>
                                <td className="border border-blue-100 px-1 py-1 text-right align-middle">
                                  {row.sgstAmount ? row.sgstAmount : "-"}
                                </td>
                                <td className="border border-blue-100 px-1 py-1 text-center align-middle">
                                  {row.cgstRate && row.cgstAmount
                                    ? `${row.cgstRate}%`
                                    : "-"}
                                </td>
                                <td className="border border-blue-100 px-1 py-1 text-right align-middle">
                                  {row.cgstAmount ? row.cgstAmount : "-"}
                                </td>
                                {/* Total Tax */}
                                <td className="border border-blue-100 px-2 py-1 text-right align-middle font-semibold">
                                  {row.totalTax}
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* Tax Amount (in words) - dynamic conversion */}
                  <div className="w-full border border-gray-200 rounded-lg bg-gray-50 px-2 py-2 my-2 flex flex-col">
                    <span
                      className="text-lg font-bold text-blue-900 mb-1"
                      style={{ fontSize: "12.5px" }}
                    >
                      Tax Amount (in words)
                    </span>
                    <span className="text-[11.5px] text-gray-700 font-mono">
                      {taxAmountInWords} /-
                    </span>
                  </div>
                </div>
                {/* BANK DETAILS ... unchanged ... */}
                <div className="w-full border border-blue-200 border-collapse mt-[4px] rounded-lg overflow-hidden flex flex-row items-stretch bg-white">
                  <div
                    className="w-1/2 border-r border-blue-200 p-2 flex flex-col justify-between bg-gray-50"
                    style={{ fontSize: "11px" }}
                  >
                    {/* REMARKS SECTION */}
                    {viewingInvoice.remarks && (
                      <div className="mb-1 mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md text-gray-800">
                        <b className="text-blue-900">Remarks:</b>
                        <div className="mt-1" style={{ whiteSpace: "pre-line" }}>
                          {viewingInvoice.remarks}
                        </div>
                      </div>
                    )}
                    {/* END REMARKS */}
                    <div>
                      <b className="text-blue-900">Company's PAN: </b>
                      <span className="font-semibold text-gray-800">AAYCA1276H</span>
                    </div>
                    <div className="mt-2">
                      <b className="text-blue-900 mb-1 block">Declaration</b>
                      <div className="text-gray-800 mt-1">
                        We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.
                      </div>
                    </div>
                  </div>
                  <div className="w-1/2 p-2 flex flex-row justify-between text-left bg-white">
                    <div className="flex flex-col w-full border border-blue-200 border-collapse mt-1 rounded-lg overflow-hidden">
                      <div className="border p-2 w-full align-top bg-gray-50 rounded-bl-lg flex flex-col" style={{ fontSize: '11px' }}>
                        <b className="text-blue-900">Bank Details</b>
                        <div className="mt-1 mb-1">A/c Holder Name :{" "}
                          {
                            companies.find((c) => c.id == invoicePreviewData.companyId)?.company_name
                          }
                        </div>
                        <div>Bank Name : <span className="font-semibold">IDFC First Bank</span></div>
                        <div>A/c No : <span className="font-semibold">10121994987</span></div>
                        <div>Branch : <span className="font-semibold">New Sanganer Road</span></div>
                      </div>
                      <div className="border border-l-0 p-2 w-full text-right align-top bg-white rounded-br-lg flex flex-col justify-between">
                        <div>
                          <div className="mb-2">For <b>
                            {
                              companies.find((c) => c.id == invoicePreviewData.companyId)?.company_name
                            }
                          </b></div>
                          {/* Signature image for company */}
                          {(() => {
                            const companyObj = companies.find(
                              (c) => String(c.id) === String(invoicePreviewData.companyId)
                            );
                            const signatureUrl = companyObj && companySignatureUrls[String(companyObj.id)];
                            if (
                              companyObj &&
                              typeof companyObj.signature === "string" &&
                              companyObj.signature.trim() !== "" &&
                              signatureUrl
                            ) {
                              return (
                                <img
                                  src={signatureUrl}
                                  alt="Authorised Signature"
                                  className="h-12 w-auto object-contain ml-auto"
                                  style={{ maxWidth: "120px" }}
                                  onError={e => { e.currentTarget.style.display = "none"; }}
                                />
                              );
                            }
                            return (
                              <div className="text-gray-400 italic text-sm text-center" style={{ fontSize: '10px' }}>
                                No signature on file
                              </div>
                            );
                          })()}
                          <div className="pt-1 font-semibold" style={{ fontSize: '10.5px' }}>Authorised Signatory</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoice;